import { useState, useEffect, useCallback } from 'react';
import {
  getActiveServiceRequests,
  acknowledgeServiceRequest,
  completeServiceRequest
} from '../services/api';
import useServiceRequestsSocket from './useServiceRequestsSocket';
import { useTranslation } from 'react-i18next';
import { toast } from '../lib/toast';

/**
 * Shared data layer for service requests: initial fetch, realtime socket
 * wiring (new / acknowledged / completed), sound-on-new, and the
 * acknowledge / complete actions with optimistic updates + toasts.
 *
 * Consumed by both the compact dashboard widget (ServiceRequestsPanel) and
 * the standalone manager page (ServiceRequests) so the two surfaces share a
 * single source of truth while rendering differently.
 */
const useServiceRequestsData = (restaurantId) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const fetchRequests = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const data = await getActiveServiceRequests(restaurantId);
      setRequests(data.data?.requests ?? data.requests ?? []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching service requests:', error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useServiceRequestsSocket(restaurantId, {
    onNewRequest: (payload) => {
      setRequests((prev) => [
        {
          id: payload.id,
          restaurant_id: payload.restaurantId,
          table_number: payload.tableNumber,
          request_type: payload.requestType,
          message: payload.message,
          priority: payload.priority || 'medium',
          status: 'pending',
          created_at: payload.createdAt
        },
        ...prev
      ]);
      if (soundEnabled) {
        try {
          const audio = new Audio('/sounds/service-request.mp3');
          audio.volume = 0.7;
          // eslint-disable-next-line promise/catch-or-return
          audio.play().catch(() => {});
        } catch (err) {
          // ignore
        }
      }
      toast.success(
        t('service.panel.newRequest', {
          defaultValue: 'New request from Table {{table}}',
          table: payload.tableNumber
        })
      );
    },
    onRequestAcknowledged: (data) => {
      setRequests((prev) =>
        prev.map((req) =>
          req.id === data.id
            ? { ...req, status: 'acknowledged', acknowledged_at: data.acknowledgedAt }
            : req
        )
      );
    },
    onRequestCompleted: (data) => {
      setRequests((prev) => prev.filter((req) => req.id !== data.id));
    }
  });

  const acknowledge = useCallback(
    async (requestId) => {
      try {
        await acknowledgeServiceRequest(requestId);
        setRequests((prev) =>
          prev.map((req) =>
            req.id === requestId
              ? { ...req, status: 'acknowledged', acknowledged_at: new Date().toISOString() }
              : req
          )
        );
        toast.success(t('service.panel.acknowledged', { defaultValue: 'Request acknowledged' }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error acknowledging request:', error);
        toast.error(
          t('service.panel.ackFailed', { defaultValue: 'Failed to acknowledge request' })
        );
      }
    },
    [t]
  );

  const complete = useCallback(
    async (requestId) => {
      try {
        await completeServiceRequest(requestId);
        setRequests((prev) => prev.filter((req) => req.id !== requestId));
        toast.success(t('service.panel.completed', { defaultValue: 'Request completed' }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error completing request:', error);
        toast.error(
          t('service.panel.completeFailed', { defaultValue: 'Failed to complete request' })
        );
      }
    },
    [t]
  );

  return { requests, loading, soundEnabled, setSoundEnabled, acknowledge, complete };
};

export default useServiceRequestsData;
